import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Loader2, Send } from 'lucide-react';
import { API } from '@/data/catalogue';

const EMPTY = { company: '', contact_name: '', email: '', phone: '', city: '', business_type: 'Showroom / Retail', message: '' };

export const Dealers = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/dealer-applications`, form);
      toast.success('Application received', {
        description: 'Our partnerships team will reach out within 2 working days.',
      });
      setForm(EMPTY);
    } catch {
      toast.error('Could not submit', { description: 'Please try again or call +91 98118 60677.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="dealers" data-testid="dealer-section" className="py-28 sm:py-36 bg-[#0D0D0F] border-y border-[#D4AF37]/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="kicker mb-4">Partners &amp; Distribution</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light">
              Put Jakson on<br />
              <span className="italic text-gold-gradient">your shelves.</span>
            </h2>
            <p className="mt-6 text-zinc-400 font-light leading-relaxed max-w-md">
              We partner with premium showrooms, sanitaryware retailers, architecture
              studios and project suppliers across India. Authorised partners receive
              display support, the April 2026 price list and priority dispatch from Bhiwadi.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-3 border border-[#D4AF37]/20 pointer-events-none hidden lg:block" />
            <form
              onSubmit={submit}
              data-testid="dealer-application-form"
              className="relative bg-[#121215] border border-[#D4AF37]/20 p-7 sm:p-9"
            >
              <div className="flex items-center gap-3 mb-7">
                <Building2 size={18} className="text-[#D4AF37]" />
                <h3 className="font-serif text-2xl font-light">Become an Authorised Partner</h3>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  required
                  data-testid="dealer-input-company"
                  value={form.company}
                  onChange={set('company')}
                  placeholder="Firm / Showroom name *"
                  className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
                />
                <input
                  required
                  data-testid="dealer-input-contact"
                  value={form.contact_name}
                  onChange={set('contact_name')}
                  placeholder="Contact person *"
                  className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
                />
                <input
                  required
                  type="email"
                  data-testid="dealer-input-email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="Email *"
                  className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
                />
                <input
                  required
                  data-testid="dealer-input-phone"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="Phone *"
                  className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
                />
                <input
                  required
                  data-testid="dealer-input-city"
                  value={form.city}
                  onChange={set('city')}
                  placeholder="City *"
                  className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
                />
                <select
                  data-testid="dealer-input-type"
                  value={form.business_type}
                  onChange={set('business_type')}
                  className="bg-[#0A0A0B] border border-zinc-800 px-4 py-3 text-sm text-zinc-300 focus:border-[#D4AF37] focus:outline-none transition-colors"
                >
                  <option>Showroom / Retail</option>
                  <option>Architecture / Interior Studio</option>
                  <option>Project Supplier</option>
                  <option>Wholesale Distributor</option>
                </select>
              </div>
              <textarea
                data-testid="dealer-input-message"
                value={form.message}
                onChange={set('message')}
                placeholder="Tell us about your business, footfall, brands you carry…"
                rows={4}
                className="mt-4 w-full bg-[#0A0A0B] border border-zinc-800 px-4 py-3 text-sm placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="dealer-submit-btn"
                className="mt-6 w-full flex items-center justify-center gap-3 bg-[#D4AF37] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#F5E6AD] transition-colors duration-300 disabled:opacity-60"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {busy ? 'Submitting…' : 'Apply for Partnership'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
